import { Box, Chip, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { InvoiceNumberSequence } from '@synergy/models';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PoolConfirmationModal, PoolFormattedMessage } from '@synergy/components-next';
import { PoolTypography, ValueOrNDash } from '@synergy/components';
import { padZeroLeft } from '@synergy/utils';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as InvoiceNumberSequenceActions from './store/invoice-number-sequence-actions';
import {
    invoiceNumberSequencesRequestingSelector,
    invoiceNumberSequencesSelector,
} from './store/invoice-number-sequence-selector';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface InvoiceNumberSequenceOverviewProps {
    onEdit: (numberSequence: InvoiceNumberSequence) => void;
}

export const InvoiceNumberSequenceOverview = (props: InvoiceNumberSequenceOverviewProps) => {
    const dispatch = useThunkDispatch();
    const invoiceNumberSequences = useAppSelector(invoiceNumberSequencesSelector);
    const requesting = useAppSelector(invoiceNumberSequencesRequestingSelector);
    const [searchParams, setSearchParams] = useSearchParams();
    const [deleteModalData, setDeleteModalData] = useState<InvoiceNumberSequence | null>(null);

    // The list sorts on the server, so the chosen column travels in the address.
    // Until one is chosen the list claims no order of its own — an arrow over a
    // column the server is not sorting by says something untrue.
    const sortColumn = searchParams.get('sort');
    const sort = sortColumn
        ? { key: sortColumn, order: ('desc' === searchParams.get('order') ? 'desc' : 'asc') as 'asc' | 'desc' }
        : undefined;

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const columns: PaloColumn<InvoiceNumberSequence>[] = [
        {
            key: 'prefix',
            type: 'classification',
            sortKey: 'prefix',
            head: <FormattedMessage id="app.common.prefix" />,
            cell: sequence => (
                <Stack>
                    <Box component="span">
                        <ValueOrNDash value={sequence.prefix} />
                    </Box>
                    {sequence.isDefault && (
                        <PoolTypography typography="label" color="primary.main" id="app.common.default" />
                    )}
                </Stack>
            ),
        },
        {
            key: 'nextFreeInvoiceNumber',
            type: 'count',
            head: <FormattedMessage id="app.configuration.invoiceNumberSequence.nextFreeInvoiceNumber" />,
            cell: sequence => padZeroLeft(sequence.nextFreeNumber, 5),
        },
        {
            key: 'suppliers',
            type: 'name',
            head: <FormattedMessage id="app.common.suppliers" />,
            cell: sequence =>
                sequence.isDefault ? (
                    <FormattedMessage id="app.configuration.invoiceNumberSequence.allSuppliers" />
                ) : (
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {sequence.suppliers.map(supplier => (
                            <Chip key={supplier.id} size="small" label={supplier.name} />
                        ))}
                    </Stack>
                ),
        },
    ];

    useEffect(() => {
        dispatch(InvoiceNumberSequenceActions.fetchInvoiceNumberSequences(searchParams));
    }, [dispatch, searchParams]);

    const handleDelete = () => {
        if (!deleteModalData?.id || deleteModalData.id < 0) {
            return;
        }
        dispatch(InvoiceNumberSequenceActions.deleteInvoiceNumberSequence(deleteModalData.id));
        setDeleteModalData(null);
    };

    return (
        <>
            <PaloListView<InvoiceNumberSequence, number>
                embedded={true}
                columns={columns}
                items={invoiceNumberSequences.items}
                getRowId={sequence => sequence.id}
                requesting={requesting}
                emptyLabel="Keine Rechnungsnummernkreise."
                rowActions={sequence => [
                    { label: <FormattedMessage id="app.common.edit" />, onClick: () => props.onEdit(sequence) },
                    // The default sequence is what every supplier without one of
                    // its own falls back to; there is no deleting it.
                    ...(sequence.isDefault
                        ? []
                        : [
                              {
                                  label: <FormattedMessage id="app.common.delete" />,
                                  onClick: () => setDeleteModalData(sequence),
                              },
                          ]),
                ]}
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                pagination={{
                    count: invoiceNumberSequences.total,
                    page: Number(searchParams.get('page') ?? 1),
                    limit: Number(searchParams.get('limit') ?? 50),
                    onPageChange: page => updateParams({ page: String(page) }),
                    onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
                }}
            />
            <PaloNextThemeProvider>
                <PoolConfirmationModal
                    open={!!deleteModalData}
                    title={<FormattedMessage id="app.configuration.invoiceNumberSequence.delete" />}
                    action={<FormattedMessage id="app.common.delete" />}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteModalData(null)}
                >
                    <PoolFormattedMessage
                        id="app.configuration.invoiceNumberSequence.delete.text"
                        values={{ number: deleteModalData?.prefix }}
                    />
                </PoolConfirmationModal>
            </PaloNextThemeProvider>
        </>
    );
};
